/* global axios */
import ApiClient from './ApiClient';

const getTimeOffset = () => -new Date().getTimezoneOffset() / 60;
const getTimezoneName = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

class ReportsAPI extends ApiClient {
  constructor() {
    super('reports', { accountScoped: true, apiVersion: 'v2' });
  }

  getReports({
    metric,
    from,
    to,
    type = 'account',
    id,
    groupBy,
    businessHours,
    excludeIssueTime,
    wlabAiOnly,
    userIds = [],
    inboxIds = [],
    labelId = null,
    teamId = null,
    vipLevelId = null,
    issueId = null,
    priority = null,
    includeMeta = false,
  }) {
    const normalizedLabelId = Number(labelId);
    const normalizedTeamId = Number(teamId);
    const normalizedVipLevelId = Number(vipLevelId);
    const normalizedIssueId = Number(issueId);
    return axios.get(`${this.url}`, {
      params: {
        metric,
        since: from,
        until: to,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
        exclude_issue_time: excludeIssueTime,
        wlab_ai_only: wlabAiOnly,
        ...(Array.isArray(userIds) && userIds.length
          ? { user_ids: userIds }
          : {}),
        ...(Array.isArray(inboxIds) && inboxIds.length
          ? { inbox_ids: inboxIds }
          : {}),
        ...(Number.isFinite(normalizedLabelId) && normalizedLabelId > 0
          ? { label_id: normalizedLabelId }
          : {}),
        ...(Number.isFinite(normalizedTeamId) && normalizedTeamId > 0
          ? { team_id: normalizedTeamId }
          : {}),
        ...(Number.isFinite(normalizedVipLevelId) && normalizedVipLevelId > 0
          ? { vip_level_id: normalizedVipLevelId }
          : {}),
        ...(Number.isFinite(normalizedIssueId) && normalizedIssueId > 0
          ? { issue_id: normalizedIssueId }
          : {}),
        ...(priority ? { priority } : {}),
        ...(includeMeta ? { include_meta: true } : {}),
      },
    });
  }

  /* eslint-disable default-param-last */
  getSummary(
    since,
    until,
    type = 'account',
    id,
    groupBy,
    businessHours,
    excludeIssueTime,
    includeMeta = false
  ) {
    return axios.get(`${this.url}/summary`, {
      params: {
        since,
        until,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
        exclude_issue_time: excludeIssueTime,
        ...(includeMeta ? { include_meta: true } : {}),
      },
    });
  }
  /* eslint-enable default-param-last */

  getConversationMetric(type = 'account', page = 1) {
    return axios.get(`${this.url}/conversations`, {
      params: {
        type,
        page,
      },
    });
  }

  getAgentReports({ from: since, to: until, businessHours, excludeIssueTime }) {
    return axios.get(`${this.url}/agents`, {
      params: {
        since,
        until,
        business_hours: businessHours,
        exclude_issue_time: excludeIssueTime,
      },
    });
  }

  getConversationTrafficCSV({ daysBefore = 6 } = {}) {
    return axios.get(`${this.url}/conversation_traffic`, {
      params: { timezone_offset: getTimeOffset(), days_before: daysBefore },
    });
  }

  getChatHistoryExport({
    since,
    until,
    agentId = null,
    inboxIds = [],
    contactId = null,
    conversationId = null,
    status = 'all',
    includeAllAgents = false,
    limit = 25000,
  } = {}) {
    const normalizedAgentId = Number(agentId);
    const normalizedInboxIds = Array.isArray(inboxIds)
      ? inboxIds.map(Number).filter(Boolean)
      : [];
    const normalizedContactId = Number(contactId);
    const normalizedLimit = Number(limit);
    const normalizedConversationId = String(conversationId || '').trim();

    return axios.get(`${this.url}/chat_history_export`, {
      params: {
        since,
        until,
        timezone_offset: getTimeOffset(),
        ...(Number.isFinite(normalizedAgentId) && normalizedAgentId > 0
          ? { agent_id: normalizedAgentId }
          : {}),
        ...(normalizedInboxIds.length === 1
          ? { inbox_id: normalizedInboxIds[0] }
          : {}),
        ...(normalizedInboxIds.length > 1
          ? { inbox_ids: normalizedInboxIds.join(',') }
          : {}),
        ...(Number.isFinite(normalizedContactId) && normalizedContactId > 0
          ? { contact_id: normalizedContactId }
          : {}),
        ...(normalizedConversationId
          ? { conversation_id: normalizedConversationId }
          : {}),
        ...(status ? { status } : {}),
        ...(Number.isFinite(normalizedAgentId) && normalizedAgentId > 0
          ? { include_all_agents: includeAllAgents }
          : {}),
        ...(Number.isFinite(normalizedLimit) && normalizedLimit > 0
          ? { limit: normalizedLimit }
          : {}),
      },
    });
  }

  getLiveChatFormsExport({
    from,
    to,
    userIds = [],
    inboxIds = [],
    teamId = null,
    limit = null,
  } = {}) {
    const normalizedLimit = Number(limit);
    const normalizedInboxIds = Array.isArray(inboxIds)
      ? inboxIds.map(Number).filter(Boolean)
      : [];
    return axios.get(`${this.url}/live_chat_forms_export`, {
      params: {
        since: from,
        until: to,
        timezone_offset: getTimeOffset(),
        user_ids: userIds,
        ...(normalizedInboxIds.length === 1
          ? { inbox_id: normalizedInboxIds[0] }
          : {}),
        ...(normalizedInboxIds.length > 1
          ? { inbox_ids: normalizedInboxIds.join(',') }
          : {}),
        team_id: teamId,
        ...(Number.isFinite(normalizedLimit) && normalizedLimit > 0
          ? { limit: normalizedLimit }
          : {}),
      },
    });
  }

  getLiveChatFormsUsageSummary({
    from,
    to,
    userIds = [],
    inboxIds = [],
    teamId = null,
    limit = null,
  } = {}) {
    const normalizedLimit = Number(limit);
    const normalizedInboxIds = Array.isArray(inboxIds)
      ? inboxIds.map(Number).filter(Boolean)
      : [];
    return axios.get(`${this.url}/live_chat_forms_usage_summary`, {
      params: {
        since: from,
        until: to,
        user_ids: userIds,
        ...(normalizedInboxIds.length === 1
          ? { inbox_id: normalizedInboxIds[0] }
          : {}),
        ...(normalizedInboxIds.length > 1
          ? { inbox_ids: normalizedInboxIds.join(',') }
          : {}),
        team_id: teamId,
        ...(Number.isFinite(normalizedLimit) && normalizedLimit > 0
          ? { limit: normalizedLimit }
          : {}),
      },
    });
  }

  getLabelReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/labels`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getInboxReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/inboxes`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getTeamReports({ from: since, to: until, businessHours, excludeIssueTime }) {
    return axios.get(`${this.url}/teams`, {
      params: {
        since,
        until,
        business_hours: businessHours,
        exclude_issue_time: excludeIssueTime,
      },
    });
  }

  getVipReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/vips`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getVipAgentReports({
    from: since,
    to: until,
    businessHours,
    excludeIssueTime,
  }) {
    return axios.get(`${this.url}/vip_agents`, {
      params: {
        since,
        until,
        business_hours: businessHours,
        exclude_issue_time: excludeIssueTime,
      },
    });
  }

  getIssueAgentReports({ from: since, to: until }) {
    return axios.get(`${this.url}/issue_agents`, {
      params: {
        since,
        until,
      },
    });
  }

  getIssueTypeReports({ from: since, to: until }) {
    return axios.get(`${this.url}/issue_types`, {
      params: {
        since,
        until,
      },
    });
  }

  getBotMetrics({ from, to, inboxIds } = {}) {
    const normalizedInboxIds = Array.isArray(inboxIds)
      ? inboxIds.map(Number).filter(Boolean)
      : [];
    return axios.get(`${this.url}/bot_metrics`, {
      params: {
        since: from,
        until: to,
        ...(normalizedInboxIds.length > 0
          ? { inbox_ids: normalizedInboxIds.join(',') }
          : {}),
      },
    });
  }

  getLineDelivery({ since, until, inboxIds = [] } = {}) {
    return axios.get(`${this.url}/line_delivery`, {
      params: {
        since,
        until,
        timezone_offset: getTimeOffset(),
        ...(Array.isArray(inboxIds) && inboxIds.length
          ? { inbox_ids: inboxIds }
          : {}),
      },
    });
  }

  getCampaignsConversion({ from, to, includeMeta = false } = {}) {
    return axios.get(`${this.url}/campaigns_conversion`, {
      params: {
        since: from,
        until: to,
        timezone_offset: getTimeOffset(),
        ...(includeMeta ? { include_meta: true } : {}),
      },
    });
  }

  getTagsUsage({
    from,
    to,
    userIds = [],
    inboxIds = [],
    teamId = null,
    labelId = null,
    vipLevelId = null,
    issueId = null,
    priority = null,
    includeMeta = false,
  } = {}) {
    const normalizedInboxIds = Array.isArray(inboxIds)
      ? inboxIds.map(Number).filter(Boolean)
      : [];
    const normalizedTeamId = Number(teamId);
    const normalizedLabelId = Number(labelId);
    const normalizedVipLevelId = Number(vipLevelId);
    const normalizedIssueId = Number(issueId);
    return axios.get(`${this.url}/tags_usage`, {
      params: {
        since: from,
        until: to,
        timezone_offset: getTimeOffset(),
        ...(Array.isArray(userIds) && userIds.length
          ? { user_ids: userIds }
          : {}),
        ...(normalizedInboxIds.length === 1
          ? { inbox_id: normalizedInboxIds[0] }
          : {}),
        ...(normalizedInboxIds.length > 1
          ? { inbox_ids: normalizedInboxIds.join(',') }
          : {}),
        ...(Number.isFinite(normalizedTeamId) && normalizedTeamId > 0
          ? { team_id: normalizedTeamId }
          : {}),
        ...(Number.isFinite(normalizedLabelId) && normalizedLabelId > 0
          ? { label_id: normalizedLabelId }
          : {}),
        ...(Number.isFinite(normalizedVipLevelId) && normalizedVipLevelId > 0
          ? { vip_level_id: normalizedVipLevelId }
          : {}),
        ...(Number.isFinite(normalizedIssueId) && normalizedIssueId > 0
          ? { issue_id: normalizedIssueId }
          : {}),
        ...(priority ? { priority } : {}),
        ...(includeMeta ? { include_meta: true } : {}),
      },
    });
  }

  getChatSatisfaction({
    from,
    to,
    userIds = [],
    inboxIds = [],
    teamId = null,
    labelId = null,
    vipLevelId = null,
    issueId = null,
    priority = null,
    includeMeta = false,
  } = {}) {
    const normalizedLabelId = Number(labelId);
    const normalizedVipLevelId = Number(vipLevelId);
    const normalizedIssueId = Number(issueId);
    const normalizedInboxIds = Array.isArray(inboxIds)
      ? inboxIds.map(Number).filter(Boolean)
      : [];
    return axios.get(`${this.url}/chat_satisfaction`, {
      params: {
        since: from,
        until: to,
        timezone_offset: getTimeOffset(),
        ...(normalizedInboxIds.length === 1
          ? { inbox_id: normalizedInboxIds[0] }
          : {}),
        ...(normalizedInboxIds.length > 1
          ? { inbox_ids: normalizedInboxIds.join(',') }
          : {}),
        team_id: teamId,
        ...(Array.isArray(userIds) && userIds.length
          ? { user_ids: userIds }
          : {}),
        ...(Number.isFinite(normalizedLabelId) && normalizedLabelId > 0
          ? { label_id: normalizedLabelId }
          : {}),
        ...(Number.isFinite(normalizedVipLevelId) && normalizedVipLevelId > 0
          ? { vip_level_id: normalizedVipLevelId }
          : {}),
        ...(Number.isFinite(normalizedIssueId) && normalizedIssueId > 0
          ? { issue_id: normalizedIssueId }
          : {}),
        ...(priority ? { priority } : {}),
        ...(includeMeta ? { include_meta: true } : {}),
      },
    });
  }

  getChatAvailability({
    from,
    to,
    userIds = [],
    inboxId = null,
    inboxIds = [],
    teamId = null,
    labelId = null,
    vipLevelId = null,
    issueId = null,
    priority = null,
    livechatOnly = false,
    includeMeta = false,
  } = {}) {
    const normalizedLabelId = Number(labelId);
    const normalizedVipLevelId = Number(vipLevelId);
    const normalizedIssueId = Number(issueId);
    const timezoneName = getTimezoneName();
    return axios.get(`${this.url}/chat_availability`, {
      params: {
        since: from,
        until: to,
        timezone_offset: getTimeOffset(),
        ...(timezoneName ? { timezone_name: timezoneName } : {}),
        ...(Array.isArray(userIds) && userIds.length
          ? { user_ids: userIds }
          : {}),
        ...(Array.isArray(inboxIds) && inboxIds.length
          ? { inbox_ids: inboxIds }
          : { inbox_id: inboxId }),
        team_id: teamId,
        ...(Number.isFinite(normalizedLabelId) && normalizedLabelId > 0
          ? { label_id: normalizedLabelId }
          : {}),
        ...(Number.isFinite(normalizedVipLevelId) && normalizedVipLevelId > 0
          ? { vip_level_id: normalizedVipLevelId }
          : {}),
        ...(Number.isFinite(normalizedIssueId) && normalizedIssueId > 0
          ? { issue_id: normalizedIssueId }
          : {}),
        ...(priority ? { priority } : {}),
        livechat_only: livechatOnly,
        ...(includeMeta ? { include_meta: true } : {}),
      },
    });
  }

  getMissedChatsAgentBreakdown({
    from,
    to,
    inboxId = null,
    inboxIds = [],
    includeMeta = true,
    includePrevious = false,
  } = {}) {
    const timezoneName = getTimezoneName();
    return axios.get(`${this.url}/missed_chats_agent_breakdown`, {
      params: {
        since: from,
        until: to,
        timezone_offset: getTimeOffset(),
        ...(timezoneName ? { timezone_name: timezoneName } : {}),
        ...(Array.isArray(inboxIds) && inboxIds.length
          ? { inbox_ids: inboxIds }
          : { inbox_id: inboxId }),
        ...(includeMeta ? { include_meta: true } : {}),
        ...(includePrevious ? { include_previous: true } : {}),
      },
    });
  }

  getChatDuration({
    from,
    to,
    userIds = [],
    inboxIds = [],
    teamId = null,
    labelId = null,
    vipLevelId = null,
    issueId = null,
    priority = null,
    includeMeta = false,
  } = {}) {
    const normalizedInboxIds = Array.isArray(inboxIds)
      ? inboxIds.map(Number).filter(Boolean)
      : [];
    const normalizedTeamId = Number(teamId);
    const normalizedLabelId = Number(labelId);
    const normalizedVipLevelId = Number(vipLevelId);
    const normalizedIssueId = Number(issueId);
    const timezoneName = getTimezoneName();
    return axios.get(`${this.url}/chat_duration`, {
      params: {
        since: from,
        until: to,
        timezone_offset: getTimeOffset(),
        ...(timezoneName ? { timezone_name: timezoneName } : {}),
        ...(Array.isArray(userIds) && userIds.length
          ? { user_ids: userIds }
          : {}),
        ...(normalizedInboxIds.length === 1
          ? { inbox_id: normalizedInboxIds[0] }
          : {}),
        ...(normalizedInboxIds.length > 1
          ? { inbox_ids: normalizedInboxIds.join(',') }
          : {}),
        ...(Number.isFinite(normalizedTeamId) && normalizedTeamId > 0
          ? { team_id: normalizedTeamId }
          : {}),
        ...(Number.isFinite(normalizedLabelId) && normalizedLabelId > 0
          ? { label_id: normalizedLabelId }
          : {}),
        ...(Number.isFinite(normalizedVipLevelId) && normalizedVipLevelId > 0
          ? { vip_level_id: normalizedVipLevelId }
          : {}),
        ...(Number.isFinite(normalizedIssueId) && normalizedIssueId > 0
          ? { issue_id: normalizedIssueId }
          : {}),
        ...(priority ? { priority } : {}),
        ...(includeMeta ? { include_meta: true } : {}),
      },
    });
  }

  getChatResponseTimes({
    from,
    to,
    userIds = [],
    inboxIds = [],
    teamId = null,
    labelId = null,
    vipLevelId = null,
    issueId = null,
    priority = null,
    includeMeta = false,
  } = {}) {
    const normalizedInboxIds = Array.isArray(inboxIds)
      ? inboxIds.map(Number).filter(Boolean)
      : [];
    const normalizedTeamId = Number(teamId);
    const normalizedLabelId = Number(labelId);
    const normalizedVipLevelId = Number(vipLevelId);
    const normalizedIssueId = Number(issueId);
    const timezoneName = getTimezoneName();
    return axios.get(`${this.url}/chat_response_times`, {
      params: {
        since: from,
        until: to,
        timezone_offset: getTimeOffset(),
        ...(timezoneName ? { timezone_name: timezoneName } : {}),
        ...(Array.isArray(userIds) && userIds.length
          ? { user_ids: userIds }
          : {}),
        ...(normalizedInboxIds.length === 1
          ? { inbox_id: normalizedInboxIds[0] }
          : {}),
        ...(normalizedInboxIds.length > 1
          ? { inbox_ids: normalizedInboxIds.join(',') }
          : {}),
        ...(Number.isFinite(normalizedTeamId) && normalizedTeamId > 0
          ? { team_id: normalizedTeamId }
          : {}),
        ...(Number.isFinite(normalizedLabelId) && normalizedLabelId > 0
          ? { label_id: normalizedLabelId }
          : {}),
        ...(Number.isFinite(normalizedVipLevelId) && normalizedVipLevelId > 0
          ? { vip_level_id: normalizedVipLevelId }
          : {}),
        ...(Number.isFinite(normalizedIssueId) && normalizedIssueId > 0
          ? { issue_id: normalizedIssueId }
          : {}),
        ...(priority ? { priority } : {}),
        ...(includeMeta ? { include_meta: true } : {}),
      },
    });
  }

  getStaffingPrediction({ from, to, userIds, inboxId, inboxIds, teamId } = {}) {
    const normalizedInboxIds = inboxIds
      ? String(inboxIds).split(',').map(Number).filter(Boolean)
      : inboxId
        ? [Number(inboxId)]
        : [];
    return axios.get(`${this.url}/staffing_prediction`, {
      params: {
        since: from,
        until: to,
        timezone_offset: getTimeOffset(),
        user_ids: userIds,
        ...(normalizedInboxIds.length === 1
          ? { inbox_id: normalizedInboxIds[0] }
          : {}),
        ...(normalizedInboxIds.length > 1
          ? { inbox_ids: normalizedInboxIds.join(',') }
          : {}),
        team_id: teamId,
      },
    });
  }

  getAgentActivity({ from, to, userIds, inboxIds, teamId } = {}) {
    const normalizedInboxIds = Array.isArray(inboxIds)
      ? inboxIds.map(Number).filter(Boolean)
      : [];
    return axios.get(`${this.url}/agent_activity`, {
      params: {
        since: from,
        until: to,
        timezone_offset: getTimeOffset(),
        user_ids: userIds,
        ...(normalizedInboxIds.length === 1
          ? { inbox_id: normalizedInboxIds[0] }
          : {}),
        ...(normalizedInboxIds.length > 1
          ? { inbox_ids: normalizedInboxIds.join(',') }
          : {}),
        team_id: teamId,
      },
    });
  }

  getAgentChatStatus({ from, to, userIds, inboxIds, teamId } = {}) {
    const normalizedInboxIds = Array.isArray(inboxIds)
      ? inboxIds.map(Number).filter(Boolean)
      : [];
    return axios.get(`${this.url}/agent_chat_status`, {
      params: {
        since: from,
        until: to,
        timezone_offset: getTimeOffset(),
        user_ids: userIds,
        ...(normalizedInboxIds.length === 1
          ? { inbox_id: normalizedInboxIds[0] }
          : {}),
        ...(normalizedInboxIds.length > 1
          ? { inbox_ids: normalizedInboxIds.join(',') }
          : {}),
        team_id: teamId,
      },
    });
  }

  getQueuedCustomersWaitingTime({
    from,
    to,
    groupBy = 'day',
    userIds,
    inboxIds = [],
    teamId,
  } = {}) {
    const normalizedInboxIds = Array.isArray(inboxIds)
      ? inboxIds.map(Number).filter(Boolean)
      : [];
    return axios.get(`${this.url}/queued_customers_waiting_time`, {
      params: {
        since: from,
        until: to,
        group_by: groupBy,
        timezone_offset: getTimeOffset(),
        user_ids: userIds,
        ...(normalizedInboxIds.length === 1
          ? { inbox_id: normalizedInboxIds[0] }
          : {}),
        ...(normalizedInboxIds.length > 1
          ? { inbox_ids: normalizedInboxIds.join(',') }
          : {}),
        team_id: teamId,
      },
    });
  }

  getTopCustomerQuestions({
    from,
    to,
    limit = 30,
    samplePerCluster = 3,
    includeMeta = false,
  } = {}) {
    return axios.get(`${this.url}/top_customer_questions`, {
      params: {
        since: from,
        until: to,
        timezone_offset: getTimeOffset(),
        limit,
        sample_per_cluster: samplePerCluster,
        ...(includeMeta ? { include_meta: true } : {}),
      },
    });
  }

  getBotSummary({
    from,
    to,
    groupBy,
    businessHours,
    wlabAiOnly,
    inboxIds,
  } = {}) {
    const normalizedInboxIds = Array.isArray(inboxIds)
      ? inboxIds.map(Number).filter(Boolean)
      : [];
    return axios.get(`${this.url}/bot_summary`, {
      params: {
        since: from,
        until: to,
        type: 'account',
        group_by: groupBy,
        business_hours: businessHours,
        wlab_ai_only: wlabAiOnly,
        ...(normalizedInboxIds.length > 0
          ? { inbox_ids: normalizedInboxIds.join(',') }
          : {}),
      },
    });
  }

  getBotUnresolvedQuestions({
    from,
    to,
    limit = 30,
    samplePerCluster = 3,
    inboxIds,
  } = {}) {
    const normalizedInboxIds = Array.isArray(inboxIds)
      ? inboxIds.map(Number).filter(Boolean)
      : [];
    return axios.get(`${this.url}/bot_unresolved_questions`, {
      params: {
        since: from,
        until: to,
        limit,
        sample_per_cluster: samplePerCluster,
        ...(normalizedInboxIds.length > 0
          ? { inbox_ids: normalizedInboxIds.join(',') }
          : {}),
      },
    });
  }

  getBotAiInsights({
    from,
    to,
    stuckMinutes = 15,
    doubleReplyWindowSeconds = 120,
    inboxIds,
  } = {}) {
    const normalizedInboxIds = Array.isArray(inboxIds)
      ? inboxIds.map(Number).filter(Boolean)
      : [];
    return axios.get(`${this.url}/bot_ai_insights`, {
      params: {
        since: from,
        until: to,
        stuck_minutes: stuckMinutes,
        double_reply_window_seconds: doubleReplyWindowSeconds,
        ...(normalizedInboxIds.length > 0
          ? { inbox_ids: normalizedInboxIds.join(',') }
          : {}),
      },
    });
  }

  createBotImproveItem({
    normalized,
    example,
    sampleConversationIds = [],
    sampleConversationDisplayIds = [],
    kind = 'unresolved_cluster',
  } = {}) {
    return axios.post(`${this.url}/bot_improve`, {
      kind,
      normalized,
      example,
      sample_conversation_ids: sampleConversationIds,
      sample_conversation_display_ids: sampleConversationDisplayIds,
    });
  }

  getBotImproveBacklog({
    from,
    to,
    status = 'all',
    assistantId = null,
    limit = 50,
  } = {}) {
    return axios.get(`${this.url}/bot_improve_backlog`, {
      params: {
        since: from,
        until: to,
        status,
        assistant_id: assistantId,
        limit,
      },
    });
  }

  getBotImproveDraft({ id } = {}) {
    return axios.get(`${this.url}/bot_improve_draft`, {
      params: { id },
    });
  }

  getBotImproveTemplateCsv({ id } = {}) {
    return axios.get(`${this.url}/bot_improve_template_csv`, {
      params: { id },
    });
  }

  applyBotImproveTemplate({ id, assistantId, dryRun = true } = {}) {
    return axios.post(`${this.url}/bot_improve_apply_template`, {
      id,
      assistant_id: assistantId,
      dry_run: dryRun,
    });
  }
}

export default new ReportsAPI();
